/// <reference types="google.maps" />
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { AGM_CORE_CONFIG, AgmCoreModule, MapLoaderService, MapsAPILoader } from 'app/agm-core';
import { GeofenceArea, LatLngPoint } from '../geofence.model';
import {
  areaSqMeters,
  boundsOfAreas,
  formatArea,
  nextAreaId,
  orientRing,
  pointInRing,
  ringCentroid,
} from '../geofence-geometry';

type DrawMode = 'none' | 'area' | 'exclusion';

/** One row of the place typeahead. */
interface PlaceSuggestion {
  primary: string;
  secondary: string;
  prediction: google.maps.places.PlacePrediction;
}

/** Keeps listbox element ids unique when two maps are on screen at once. */
let mapInstanceSeq = 0;

/**
 * Map surface for drawing and reading a geofence.
 *
 * Owns exactly one job: turn `GeofenceArea[]` into editable Google Maps
 * polygons and back. It knows nothing about revisions, members or saving.
 *
 * Rendering: one `google.maps.Polygon` per area, with the holes as additional
 * paths on the same polygon so exclusions genuinely punch through the fill
 * (Google renders a path wound opposite to the outer ring as a hole). Google's
 * own editing handles then work on holes for free.
 *
 * Structure changes (delete an area, delete one exclusion, zoom to one) are
 * driven from the list beside the map rather than from map hit-testing, which
 * cannot reliably tell "clicked the hole" from "clicked the area".
 *
 * Provides its own Maps loader rather than relying on the host route's
 * `provideAgmCore()`. `DialogService` builds dialogs from the ROOT injector, so a
 * route-level provider is not visible to this component when it is rendered
 * inside the assignment panel. Same shape and key as `SelectAddressComponent`,
 * which solved this first — matching `libraries` matters, because the underlying
 * js-api-loader complains if it is constructed twice with different options.
 */
@Component({
  selector: 'app-geofence-map',
  standalone: true,
  imports: [CommonModule, FormsModule, AgmCoreModule],
  templateUrl: './geofence-map.component.html',
  styleUrls: ['./geofence-map.component.scss'],
  providers: [
    {
      provide: AGM_CORE_CONFIG,
      useValue: {
        apiKey: 'AIzaSyASc6o_48dOJz22fV6isqrMx91GsfUqqk4',
        libraries: ['places'],
      },
      multi: true,
    },
    MapLoaderService,
    MapsAPILoader,
  ],
})
export class GeofenceMapComponent implements AfterViewInit, OnChanges, OnDestroy {
  /** Areas to render. In edit mode the component works on its own copy. */
  @Input() areas: GeofenceArea[] = [];
  /** Read-only mode: no toolbar, no editing handles. Used by the member map view. */
  @Input() readonly = false;
  @Input() heightPx = 460;
  /** Where to centre when there is nothing drawn yet. Defaults to India. */
  @Input() fallbackCenter: LatLngPoint = { lat: 20.5937, lng: 78.9629 };

  @Output() areasChange = new EventEmitter<GeofenceArea[]>();

  @ViewChild('mapContainer', { static: true }) mapContainer!: ElementRef<HTMLDivElement>;

  drawMode: DrawMode = 'none';
  /** Free-text place lookup, so an admin can get to the right city before drawing. */
  placeQuery = '';
  searching = false;
  /** Live typeahead rows, refreshed as the admin types. */
  suggestions: PlaceSuggestion[] = [];
  /** Keyboard highlight into `suggestions`; -1 means nothing is highlighted. */
  activeSuggestion = -1;
  /** Stable id for the listbox, so the input can point `aria-controls` at it. */
  readonly listboxId = `geofence-places-${(mapInstanceSeq += 1)}`;
  /** Shown only when a query is ambiguous; picking one flies the map to it. */
  placeResults: Array<{ label: string; result: google.maps.GeocoderResult }> = [];
  /** Shown under the toolbar while drawing, and for discarded exclusions. */
  hint = '';
  mapReady = false;
  loadError = '';
  selectedAreaId: string | null = null;

  private map?: google.maps.Map;
  private geocoder?: google.maps.Geocoder;
  /**
   * Set once the map is up, and cleared for good if a predictions request is
   * refused — the typeahead needs Places API (New) on the key while the geocode
   * behind `Go` does not, so a refusal degrades to search-on-submit instead of
   * leaving the admin with a box that looks broken.
   */
  private suggestionsAvailable = false;
  /** Billing session for one type-then-pick run; `fetchFields` spends it. */
  private placeSession?: google.maps.places.AutocompleteSessionToken;
  private suggestTimer: ReturnType<typeof setTimeout> | null = null;
  /** Rising counter so a slow response cannot overwrite a newer keystroke's. */
  private suggestSeq = 0;
  /** Vertices of the shape currently being drawn, in click order. */
  private draftPoints: LatLngPoint[] = [];
  private draftPolygon?: google.maps.Polygon;
  private readonly drawListeners: google.maps.MapsEventListener[] = [];
  private readonly polygons = new Map<string, google.maps.Polygon>();
  private readonly pathListeners: google.maps.MapsEventListener[] = [];
  private destroyed = false;
  /** Guards the model -> map -> model echo while we rewrite polygon paths. */
  private renderingFromModel = false;
  /**
   * The array reference this component last pushed out. The parent stores it
   * straight back into the `[areas]` binding, so `ngOnChanges` fires for a
   * change the map already applied — re-rendering there would rebuild the very
   * polygon the user is dragging.
   */
  private selfEmitted: GeofenceArea[] | null = null;
  private pathSyncTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private mapsLoader: MapsAPILoader,
    private zone: NgZone,
  ) {}

  // ------------------------------------------------------------- lifecycle

  async ngAfterViewInit(): Promise<void> {
    try {
      await this.mapsLoader.load();
      if (this.destroyed) return;
      await this.initMap();
    } catch (error: any) {
      console.error('Geofence map failed to load', error);
      this.zone.run(() => (this.loadError = 'The map could not be loaded. Check your connection and reload.'));
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['areas'] && this.mapReady && changes['areas'].currentValue !== this.selfEmitted) {
      this.renderAreas();
      // Read-only callers swap the whole shape when the viewer picks a different
      // geofence, so always re-frame. While editing, only frame the first shape —
      // re-fitting mid-edit would yank the viewport out from under the drawing.
      if (this.readonly || changes['areas'].firstChange || !changes['areas'].previousValue?.length) {
        this.fitToAreas();
      }
    }
    if (changes['readonly'] && this.mapReady) {
      this.polygons.forEach(p => p.setEditable(!this.readonly));
      if (this.readonly) this.setDrawMode('none');
    }
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    if (this.pathSyncTimer) clearTimeout(this.pathSyncTimer);
    if (this.suggestTimer) clearTimeout(this.suggestTimer);
    this.clearPathListeners();
    this.drawListeners.splice(0).forEach(listener => listener.remove());
    this.polygons.forEach(p => p.setMap(null));
    this.polygons.clear();
    this.clearDraft();
  }

  // ------------------------------------------------------------ map set-up

  private async initMap(): Promise<void> {
    const bounds = boundsOfAreas(this.areas);
    this.map = new google.maps.Map(this.mapContainer.nativeElement, {
      center: this.fallbackCenter,
      zoom: bounds ? 12 : 5,
      mapTypeId: google.maps.MapTypeId.ROADMAP,
      mapTypeControl: true,
      streetViewControl: false,
      fullscreenControl: true,
      clickableIcons: false,
    });

    this.geocoder = new google.maps.Geocoder();
    this.suggestionsAvailable =
      typeof google.maps.places?.AutocompleteSuggestion?.fetchAutocompleteSuggestions === 'function';

    this.mapReady = true;
    this.renderAreas();
    this.fitToAreas();
  }

  // ---------------------------------------------------------- place search

  /**
   * Two ways to move the map: a live typeahead, and a geocode behind `Go` for
   * anything the typeahead cannot answer.
   *
   * Both are BIASED to the current viewport, never restricted to a country. The
   * admin has almost always panned near the territory before typing, so the
   * viewport is a better prior than any fixed region — and a hard restriction
   * would make an overseas territory unfindable while reporting it as "no place
   * found", a dead end the admin cannot debug. `fallbackCenter` already opens
   * the map over India, so the common case biases there for free.
   *
   * `AutocompleteSuggestion` rather than the `Autocomplete` widget: the widget
   * is closed to new customers and renders its own dropdown into the document,
   * which fights the map's stacking context. Fetching predictions keeps the list
   * ours to style and to keyboard-drive.
   */
  onQueryChange(value: string): void {
    this.placeQuery = value;
    this.placeResults = [];
    this.hint = '';
    this.activeSuggestion = -1;

    if (this.suggestTimer) clearTimeout(this.suggestTimer);
    const query = value.trim();

    // Under three characters cannot pick out a place, and every keystroke bills.
    if (!this.suggestionsAvailable || query.length < 3) {
      this.suggestions = [];
      return;
    }
    this.suggestTimer = setTimeout(() => void this.fetchSuggestions(query), 250);
  }

  onQueryKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.closeSuggestions();
      return;
    }
    if (!this.suggestions.length) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.activeSuggestion = (this.activeSuggestion + 1) % this.suggestions.length;
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.activeSuggestion = (this.activeSuggestion + this.suggestions.length - 1) % this.suggestions.length;
    }
  }

  /** Enter takes the highlighted suggestion if there is one, else geocodes. */
  submitSearch(): void {
    const highlighted = this.suggestions[this.activeSuggestion];
    if (highlighted) {
      void this.chooseSuggestion(highlighted);
      return;
    }
    void this.searchPlace();
  }

  closeSuggestions(): void {
    this.suggestions = [];
    this.activeSuggestion = -1;
  }

  private async fetchSuggestions(query: string): Promise<void> {
    if (!this.map || this.destroyed || query !== this.placeQuery.trim()) return;

    const seq = (this.suggestSeq += 1);
    if (!this.placeSession) this.placeSession = new google.maps.places.AutocompleteSessionToken();

    const request: google.maps.places.AutocompleteRequest = {
      input: query,
      sessionToken: this.placeSession,
    };
    // `getBounds()` is undefined until the map's first idle, so a very early
    // keystroke searches unbiased rather than passing undefined through.
    const bounds = this.map.getBounds();
    if (bounds) request.locationBias = bounds;

    try {
      const { suggestions } = await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request);
      if (this.destroyed || seq !== this.suggestSeq) return;

      this.suggestions = suggestions
        .map(suggestion => suggestion.placePrediction)
        .filter((prediction): prediction is google.maps.places.PlacePrediction => !!prediction)
        .slice(0, 6)
        .map(prediction => ({
          primary: prediction.mainText?.text || prediction.text.text,
          secondary: prediction.secondaryText?.text || '',
          prediction,
        }));
      this.activeSuggestion = -1;
    } catch (error) {
      if (this.destroyed || seq !== this.suggestSeq) return;
      // Most likely Places API (New) is not enabled on the key. Stop asking —
      // `Go` still geocodes, so the box keeps working.
      console.warn('Place suggestions unavailable — falling back to search on submit', error);
      this.suggestionsAvailable = false;
      this.suggestions = [];
    }
  }

  async chooseSuggestion(suggestion: PlaceSuggestion): Promise<void> {
    if (this.suggestTimer) clearTimeout(this.suggestTimer);
    this.closeSuggestions();
    if (!this.map) return;

    const label = [suggestion.primary, suggestion.secondary].filter(Boolean).join(', ');
    this.placeQuery = label;

    try {
      // `fetchFields` concludes the billing session, so the token is spent.
      const { place } = await suggestion.prediction.toPlace().fetchFields({
        fields: ['location', 'viewport', 'formattedAddress'],
      });
      this.placeSession = undefined;
      if (this.destroyed || !this.map) return;

      if (place.viewport) {
        this.map.fitBounds(place.viewport, 24);
      } else if (place.location) {
        this.map.setCenter(place.location);
        this.map.setZoom(16);
      } else {
        // A prediction with no geometry is rare but real; let the geocode try.
        await this.searchPlace();
        return;
      }
      this.hint = `Showing ${place.formattedAddress || label}. Use Add area to draw here.`;
    } catch (error) {
      console.warn('Could not open that place — falling back to a geocode', error);
      this.placeSession = undefined;
      if (!this.destroyed) await this.searchPlace();
    }
  }

  async searchPlace(): Promise<void> {
    const query = this.placeQuery.trim();
    if (!query || !this.geocoder || !this.map || this.searching) return;

    this.closeSuggestions();
    this.searching = true;
    this.placeResults = [];
    this.hint = '';

    try {
      const request: google.maps.GeocoderRequest = { address: query };
      const bounds = this.map.getBounds();
      if (bounds) request.bounds = bounds;

      const response = await this.geocoder.geocode(request);
      const results = (response?.results || []).slice(0, 5);

      if (!results.length) {
        this.hint = `No place found for “${query}”. Try adding the city or a landmark.`;
        return;
      }
      if (results.length === 1) {
        this.goToPlace(results[0]);
        return;
      }
      this.placeResults = results.map(result => ({ label: result.formatted_address, result }));
    } catch (error: any) {
      // ZERO_RESULTS rejects rather than resolving empty, so it lands here too.
      const status = error?.code || error?.message || '';
      this.hint = String(status).includes('ZERO_RESULTS')
        ? `No place found for “${query}”. Try adding the city or a landmark.`
        : 'Could not search for that place right now. Check your connection and try again.';
    } finally {
      this.searching = false;
    }
  }

  goToPlace(result: google.maps.GeocoderResult): void {
    this.placeResults = [];
    if (!this.map) return;

    if (result.geometry?.viewport) {
      this.map.fitBounds(result.geometry.viewport, 24);
    } else if (result.geometry?.location) {
      this.map.setCenter(result.geometry.location);
      this.map.setZoom(16);
    }
    this.hint = `Showing ${result.formatted_address}. Use Add area to draw here.`;
  }

  clearPlaceSearch(): void {
    if (this.suggestTimer) clearTimeout(this.suggestTimer);
    this.placeQuery = '';
    this.placeResults = [];
    this.placeSession = undefined;
    this.closeSuggestions();
  }

  // --------------------------------------------------------------- drawing

  startDrawArea(): void {
    this.setDrawMode(this.drawMode === 'area' ? 'none' : 'area');
  }

  startDrawExclusion(): void {
    if (!this.areas.length) {
      this.hint = 'Draw an area first — an exclusion has to sit inside one.';
      return;
    }
    this.setDrawMode(this.drawMode === 'exclusion' ? 'none' : 'exclusion');
  }

  /**
   * Drawing is hand-rolled on plain map clicks.
   *
   * `google.maps.drawing.DrawingManager` was REMOVED from the Maps JavaScript API
   * in v3.65, and the app-wide loader is pinned to `weekly` — so there is no
   * version of it left to call. Clicks build a draft ring, and the toolbar
   * finishes or cancels it; double-click finishes as a shortcut.
   */
  private setDrawMode(mode: DrawMode): void {
    this.drawMode = mode;
    this.clearDraft();

    this.hint =
      mode === 'area'
        ? 'Click each corner of the area on the map, then choose Finish area.'
        : mode === 'exclusion'
          ? 'Click each corner of the area to exclude, then choose Finish. It has to sit inside an area you have already drawn.'
          : '';

    this.drawListeners.splice(0).forEach(listener => listener.remove());
    if (!this.map) return;

    // Double-click zoom would fight the double-click-to-finish shortcut.
    this.map.setOptions({ disableDoubleClickZoom: mode !== 'none' });
    if (mode === 'none') return;

    this.drawListeners.push(
      this.map.addListener('click', (event: google.maps.MapMouseEvent) => {
        if (!event.latLng) return;
        this.zone.run(() => this.addDraftPoint({ lat: event.latLng!.lat(), lng: event.latLng!.lng() }));
      }),
      this.map.addListener('dblclick', () => this.zone.run(() => this.finishDrawing())),
    );
  }

  private addDraftPoint(point: LatLngPoint): void {
    this.draftPoints = [...this.draftPoints, point];
    this.renderDraft();
  }

  undoLastPoint(): void {
    this.draftPoints = this.draftPoints.slice(0, -1);
    this.renderDraft();
  }

  cancelDrawing(): void {
    this.setDrawMode('none');
  }

  get draftPointCount(): number {
    return this.draftPoints.length;
  }

  get canFinishDrawing(): boolean {
    return this.dedupedDraft().length >= 3;
  }

  /**
   * A double-click emits two `click` events first, so the last one or two points
   * land on top of each other. Drop consecutive near-identical points rather than
   * shipping a degenerate ring.
   */
  private dedupedDraft(): LatLngPoint[] {
    const epsilon = 1e-7;
    return this.draftPoints.filter((point, index, all) => {
      if (index === 0) return true;
      const previous = all[index - 1];
      return Math.abs(point.lat - previous.lat) > epsilon || Math.abs(point.lng - previous.lng) > epsilon;
    });
  }

  finishDrawing(): void {
    const ring = this.dedupedDraft();
    if (ring.length < 3) {
      this.hint = 'An area needs at least 3 corners. Keep clicking, or cancel.';
      return;
    }
    this.onPolygonDrawn(ring);
  }

  /** Draws the in-progress shape so the admin can see what they are placing. */
  private renderDraft(): void {
    if (!this.map) return;

    if (!this.draftPoints.length) {
      this.draftPolygon?.setMap(null);
      this.draftPolygon = undefined;
      return;
    }

    const path = this.draftPoints.map(p => ({ lat: p.lat, lng: p.lng }));
    if (!this.draftPolygon) {
      this.draftPolygon = new google.maps.Polygon({
        paths: path,
        map: this.map,
        strokeColor: this.drawMode === 'exclusion' ? '#b3261e' : '#1976d2',
        strokeOpacity: 0.95,
        strokeWeight: 2,
        fillColor: this.drawMode === 'exclusion' ? '#b3261e' : '#1976d2',
        fillOpacity: 0.12,
        clickable: false,
        editable: false,
        zIndex: 3,
      });
    } else {
      this.draftPolygon.setPath(path);
    }
  }

  private clearDraft(): void {
    this.draftPoints = [];
    this.draftPolygon?.setMap(null);
    this.draftPolygon = undefined;
  }

  private onPolygonDrawn(ring: LatLngPoint[]): void {
    const mode = this.drawMode;
    this.setDrawMode('none');

    if (ring.length < 3) {
      this.hint = 'That shape needs at least 3 points. Try again.';
      return;
    }

    if (mode === 'exclusion') {
      const centroid = ringCentroid(ring);
      const host = centroid ? this.areas.find(a => pointInRing(centroid, a.outer)) : undefined;
      if (!host) {
        this.hint = 'That exclusion is not inside any area, so it was discarded. Draw it inside an area.';
        return;
      }
      this.commit(
        this.areas.map(a => (a.id === host.id ? { ...a, holes: [...a.holes, ring] } : a)),
        host.id,
      );
      return;
    }

    const area: GeofenceArea = { id: nextAreaId(), outer: ring, holes: [] };
    this.commit([...this.areas, area], area.id);
  }

  // ------------------------------------------------------ structure edits

  removeArea(area: GeofenceArea): void {
    this.commit(
      this.areas.filter(a => a.id !== area.id),
      null,
    );
  }

  removeHole(area: GeofenceArea, holeIndex: number): void {
    this.commit(
      this.areas.map(a => (a.id === area.id ? { ...a, holes: a.holes.filter((_, i) => i !== holeIndex) } : a)),
      area.id,
    );
  }

  selectArea(area: GeofenceArea): void {
    this.selectedAreaId = this.selectedAreaId === area.id ? null : area.id;
    this.applySelectionStyles();
    if (this.selectedAreaId) this.zoomToArea(area);
  }

  zoomToArea(area: GeofenceArea): void {
    const bounds = boundsOfAreas([area]);
    if (!bounds || !this.map) return;
    this.map.fitBounds(
      new google.maps.LatLngBounds(
        { lat: bounds.south, lng: bounds.west },
        { lat: bounds.north, lng: bounds.east },
      ),
      48,
    );
  }

  fitToAreas(): void {
    const bounds = boundsOfAreas(this.areas);
    if (!this.map) return;
    if (!bounds) {
      this.map.setCenter(this.fallbackCenter);
      this.map.setZoom(5);
      return;
    }
    this.map.fitBounds(
      new google.maps.LatLngBounds(
        { lat: bounds.south, lng: bounds.west },
        { lat: bounds.north, lng: bounds.east },
      ),
      // The read-only preview is short; 48px of padding on it would zoom so far
      // out that the shape becomes a speck.
      this.readonly ? 16 : 48,
    );
  }

  // ------------------------------------------------------------- rendering

  private renderAreas(): void {
    if (!this.map) return;
    this.renderingFromModel = true;
    this.clearPathListeners();

    const liveIds = new Set(this.areas.map(a => a.id));
    this.polygons.forEach((polygon, id) => {
      if (!liveIds.has(id)) {
        polygon.setMap(null);
        this.polygons.delete(id);
      }
    });

    this.areas.forEach(area => {
      const paths = this.areaToPaths(area);
      let polygon = this.polygons.get(area.id);
      if (!polygon) {
        polygon = new google.maps.Polygon({
          ...this.polygonStyle(area.id === this.selectedAreaId, !this.readonly),
          paths,
          map: this.map,
        });
        polygon.addListener('click', () => this.zone.run(() => this.selectArea(area)));
        this.polygons.set(area.id, polygon);
      } else {
        polygon.setPaths(paths);
        polygon.setOptions(this.polygonStyle(area.id === this.selectedAreaId, !this.readonly));
      }
      if (!this.readonly) this.watchPolygonPaths(area.id, polygon);
    });

    this.renderingFromModel = false;
  }

  private applySelectionStyles(): void {
    this.polygons.forEach((polygon, id) =>
      polygon.setOptions(this.polygonStyle(id === this.selectedAreaId, !this.readonly)),
    );
  }

  private polygonStyle(selected: boolean, editable: boolean): google.maps.PolygonOptions {
    return {
      strokeColor: selected ? '#0b5ed7' : '#1976d2',
      strokeOpacity: 0.95,
      strokeWeight: selected ? 3 : 2,
      fillColor: '#1976d2',
      fillOpacity: selected ? 0.24 : 0.16,
      editable,
      draggable: false,
      clickable: true,
      zIndex: selected ? 2 : 1,
    };
  }

  /** Outer clockwise, holes counter-clockwise — Google's hole convention. */
  private areaToPaths(area: GeofenceArea): google.maps.LatLngLiteral[][] {
    const rings = [orientRing(area.outer, false), ...area.holes.map(h => orientRing(h, true))];
    return rings.map(ring => ring.map(p => ({ lat: p.lat, lng: p.lng })));
  }

  private pathToRing(path: google.maps.MVCArray<google.maps.LatLng>): LatLngPoint[] {
    return path.getArray().map(latLng => ({ lat: latLng.lat(), lng: latLng.lng() }));
  }

  /**
   * Mirror Google's own vertex edits back into the model. Path 0 is the outer
   * ring; the rest are holes, in the order `areaToPaths` laid them down.
   */
  private watchPolygonPaths(areaId: string, polygon: google.maps.Polygon): void {
    polygon.getPaths().forEach(path => {
      ['insert_at', 'remove_at', 'set_at'].forEach(event => {
        this.pathListeners.push(
          google.maps.event.addListener(path, event, () => {
            if (this.renderingFromModel) return;
            this.scheduleSync(areaId, polygon);
          }),
        );
      });
    });
  }

  private clearPathListeners(): void {
    this.pathListeners.splice(0).forEach(listener => listener.remove());
  }

  /**
   * Google fires `set_at` continuously while a vertex is dragged. Coalescing
   * them keeps the parent's geometry validation off the drag's critical path.
   */
  private scheduleSync(areaId: string, polygon: google.maps.Polygon): void {
    if (this.pathSyncTimer) clearTimeout(this.pathSyncTimer);
    this.pathSyncTimer = setTimeout(() => {
      this.pathSyncTimer = null;
      if (this.destroyed) return;
      this.zone.run(() => this.syncAreaFromPolygon(areaId, polygon));
    }, 120);
  }

  private syncAreaFromPolygon(areaId: string, polygon: google.maps.Polygon): void {
    const paths = polygon.getPaths().getArray();
    if (!paths.length) return;
    const outer = this.pathToRing(paths[0]);
    const holes = paths.slice(1).map(p => this.pathToRing(p));
    // Vertex drags do not change structure, so the polygons on screen are already
    // correct — emit without a re-render to avoid fighting the drag in progress.
    this.areas = this.areas.map(a => (a.id === areaId ? { ...a, outer, holes } : a));
    this.selfEmitted = this.areas;
    this.areasChange.emit(this.areas);
  }

  private commit(areas: GeofenceArea[], selectId: string | null): void {
    this.areas = areas;
    this.selectedAreaId = selectId;
    this.renderAreas();
    this.selfEmitted = this.areas;
    this.areasChange.emit(this.areas);
  }

  // --------------------------------------------------------------- display

  areaLabel(index: number): string {
    return `Area ${index + 1}`;
  }

  areaSize(area: GeofenceArea): string {
    return formatArea(areaSqMeters(area));
  }

  trackByAreaId(_index: number, area: GeofenceArea): string {
    return area.id;
  }
}
